import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import {
  FileText, Plus, Search, Filter, AlertTriangle,
  Clock, CheckCircle, XCircle, RefreshCw, Upload,
  Trash2, Edit, Download, Eye, FilePlus, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';

const ContractManagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canView = hasPermission(user?.role, 'contracts:view') || hasPermission(user?.role, 'contracts:full');
  const canEdit = hasPermission(user?.role, 'contracts:full');

  const [activeTab, setActiveTab] = useState('contracts');
  const [showModal, setShowModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const [formData, setFormData] = useState({
    user_id: '',
    contract_type: 'CLT',
    start_date: '',
    end_date: '',
    is_indefinite: false,
    salary_type: 'MONTHLY',
    salary_amount: '',
    workload_weekly: 44,
    notes: '',
    is_trial_period: false,
    trial_period_days_1: 45,
    trial_period_days_2: 45,
  });

  const [renewData, setRenewData] = useState({
    new_start_date: '',
    new_end_date: '',
    new_salary_amount: ''
  });

  const [documentData, setDocumentData] = useState({
    document_type: '',
    document: null
  });

  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [contractToTerminate, setContractToTerminate] = useState(null);
  const [contractToDelete, setContractToDelete] = useState(null);

  // Buscar contratos
  const { data: contractsData, isLoading: loadingContracts, refetch: refetchContracts } = useQuery({
    queryKey: ['contracts', filterStatus, filterType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('contractType', filterType);
      const { data } = await api.get(`/contracts?${params.toString()}`);
      return data;
    },
    enabled: canView
  });

  // Buscar alertas
  const { data: alertsData } = useQuery({
    queryKey: ['contract-alerts'],
    queryFn: async () => {
      const { data } = await api.get('/contracts/alerts');
      return data;
    },
    enabled: canView
  });

  // Buscar contratos próximos do vencimento
  const { data: expiringData } = useQuery({
    queryKey: ['expiring-contracts'],
    queryFn: async () => {
      const { data } = await api.get('/contracts/expired?days=30');
      return data;
    },
    enabled: canView && activeTab === 'alerts'
  });

  // Buscar usuários ativos
  const { data: usersData } = useQuery({
    queryKey: ['users-active'],
    queryFn: async () => {
      const { data } = await api.get('/contracts/users');
      return data;
    },
    enabled: canEdit
  });

  const contracts = contractsData?.data || [];
  const alerts = alertsData?.data || [];
  const expiringContracts = expiringData?.data || [];
  const users = usersData?.data || [];

  // Filtrar contratos por busca
  const filteredContracts = contracts.filter(contract =>
    contract.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.user_cpf?.includes(searchTerm)
  );

  // Mutation para criar contrato
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/contracts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contrato criado com sucesso!');
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar contrato');
    }
  });

  // Mutation para atualizar contrato
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/contracts/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contrato atualizado com sucesso!');
      setShowModal(false);
      setSelectedContract(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar contrato');
    }
  });

  // Mutation para deletar contrato
  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/contracts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contrato deletado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar contrato');
    }
  });

  // Mutation para renovar contrato
  const renewMutation = useMutation({
    mutationFn: ({ id, data }) => api.post(`/contracts/${id}/renew`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contrato renovado com sucesso!');
      setShowRenewModal(false);
      setSelectedContract(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao renovar contrato');
    }
  });

  // Mutation para adicionar documento
  const addDocumentMutation = useMutation({
    mutationFn: ({ id, formData }) => api.post(`/contracts/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Documento adicionado com sucesso!');
      setShowDocumentModal(false);
      setDocumentData({ document_type: '', document: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao adicionar documento');
    }
  });

  // Mutation para terminar contrato
  const terminateMutation = useMutation({
    mutationFn: (id) => api.put(`/contracts/${id}`, { status: 'TERMINATED' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contrato terminado!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao terminar contrato');
    }
  });

  // Mutation para ação sobre contrato de experiência (Prorrogar / Efetivar / Encerrar)
  const trialActionMutation = useMutation({
    mutationFn: ({ id, action, notes }) => api.post(`/contracts/${id}/trial-action`, { action, notes }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-alerts'] });
      toast.success(res?.data?.message || 'Ação de experiência realizada com sucesso!');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Erro ao processar ação de experiência');
    }
  });

  const resetForm = () => {
    setFormData({
      user_id: '',
      contract_type: 'CLT',
      start_date: '',
      end_date: '',
      is_indefinite: false,
      salary_type: 'MONTHLY',
      salary_amount: '',
      workload_weekly: 44,
      notes: '',
      is_trial_period: false,
      trial_period_days_1: 45,
      trial_period_days_2: 45,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    updateMutation.mutate({ id: selectedContract.id, data: formData });
  };

  const handleRenew = (e) => {
    e.preventDefault();
    renewMutation.mutate({ id: selectedContract.id, data: renewData });
  };

  const handleDocumentUpload = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('document_type', documentData.document_type);
    fd.append('document', documentData.document);
    addDocumentMutation.mutate({ id: selectedContract.id, formData: fd });
  };

  const openEditModal = (contract) => {
    setSelectedContract(contract);
    setFormData({
      user_id: contract.user_id,
      contract_type: contract.contract_type,
      start_date: contract.start_date?.split('T')[0] || '',
      end_date: contract.end_date?.split('T')[0] || '',
      is_indefinite: contract.is_indefinite,
      salary_type: contract.salary_type || 'MONTHLY',
      salary_amount: contract.salary_amount || '',
      workload_weekly: contract.workload_weekly || 44,
      notes: contract.notes || '',
      is_trial_period: contract.is_trial_period || false,
      trial_period_days_1: contract.trial_period_days_1 || 45,
      trial_period_days_2: contract.trial_period_days_2 || 45,
    });
    setShowModal(true);
  };

  const openRenewModal = (contract) => {
    setSelectedContract(contract);
    setRenewData({
      new_start_date: contract.end_date ? new Date(new Date(contract.end_date).getTime() + 86400000).toISOString().split('T')[0] : '',
      new_end_date: '',
      new_salary_amount: contract.salary_amount || ''
    });
    setShowRenewModal(true);
  };

  const openDocumentModal = (contract) => {
    setSelectedContract(contract);
    setShowDocumentModal(true);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      EXPIRED: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      TERMINATED: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle },
      RENEWED: { bg: 'bg-blue-100', text: 'text-blue-800', icon: RefreshCw }
    };
    const config = statusMap[status] || statusMap.ACTIVE;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getContractTypeLabel = (type) => {
    const labels = {
      CLT: 'CLT',
      PJ: 'Pessoa Jurídica',
      ESTAGIO: 'Estágio',
      TEMPORARIO: 'Temporário'
    };
    return labels[type] || type;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          Você não tem permissão para acessar esta área.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contratos de Trabalho</h1>
          <p className="text-gray-600 mt-1">Gerencie contratos de trabalho dos funcionários</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { resetForm(); setSelectedContract(null); setShowModal(true); }}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Novo Contrato
          </button>
        )}
      </div>

      {/* Abas */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('contracts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'contracts'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FileText className="w-5 h-5 inline mr-2" />
            Contratos
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'alerts'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <AlertTriangle className="w-5 h-5 inline mr-2" />
            Alertas
            {alerts.reduce((acc, a) => acc + a.count, 0) > 0 && (
              <span className="ml-2 bg-red-100 text-red-800 py-0.5 px-2 rounded-full text-xs">
                {alerts.reduce((acc, a) => acc + a.count, 0)}
              </span>
            )}
          </button>
        </nav>
      </div>

      {activeTab === 'contracts' && (
        <>
          {/* Filtros */}
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nome ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos os status</option>
              <option value="ACTIVE">Ativo</option>
              <option value="EXPIRED">Vencido</option>
              <option value="TERMINATED">Terminado</option>
              <option value="RENEWED">Renovado</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos os tipos</option>
              <option value="CLT">CLT</option>
              <option value="PJ">Pessoa Jurídica</option>
              <option value="ESTAGIO">Estágio</option>
              <option value="TEMPORARIO">Temporário</option>
            </select>
          </div>

          {/* Tabela de Contratos */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Início</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Término</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carga Semanal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingContracts ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      Carregando...
                    </td>
                  </tr>
                ) : filteredContracts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      Nenhum contrato encontrado
                    </td>
                  </tr>
                ) : (
                  filteredContracts.map((contract) => (
                    <tr key={contract.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{contract.user_name}</div>
                        <div className="text-sm text-gray-500">{contract.user_cpf}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{getContractTypeLabel(contract.contract_type)}</div>
                        {contract.is_trial_period && (
                          <div className="mt-1">
                            {contract.trial_status === 'FIRST_PERIOD' && (
                              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                1º Per. Exp. ({contract.trial_period_days_1 || 45}d)
                              </span>
                            )}
                            {contract.trial_status === 'EXTENDED' && (
                              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-800 border border-purple-300">
                                2º Per. Exp. ({contract.trial_period_days_2 || 45}d)
                              </span>
                            )}
                            {contract.trial_status === 'CONVERTED_INDEFINITE' && (
                              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                Efetivado
                              </span>
                            )}
                            {contract.trial_status === 'TERMINATED_IN_TRIAL' && (
                              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700">
                                Término Exp.
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(contract.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.is_indefinite ? 'Indeterminado' : formatDate(contract.end_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(contract.salary_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.workload_weekly ? `${contract.workload_weekly}h` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(contract.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2 items-center">
                          {canEdit && (
                            <>
                              {contract.is_trial_period && contract.status === 'ACTIVE' && (
                                <>
                                  {contract.trial_status === 'FIRST_PERIOD' && (
                                    <button
                                      onClick={() => trialActionMutation.mutate({ id: contract.id, action: 'EXTEND' })}
                                      disabled={trialActionMutation.isPending}
                                      className="px-2 py-0.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 font-semibold transition-colors"
                                      title="Prorrogar experiência para o 2º período (+45 dias)"
                                    >
                                      Prorrogar
                                    </button>
                                  )}
                                  {(contract.trial_status === 'FIRST_PERIOD' || contract.trial_status === 'EXTENDED') && (
                                    <button
                                      onClick={() => trialActionMutation.mutate({ id: contract.id, action: 'CONVERT_INDEFINITE' })}
                                      disabled={trialActionMutation.isPending}
                                      className="px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 font-semibold transition-colors"
                                      title="Efetivar colaborador em definitivo (Prazo Indeterminado)"
                                    >
                                      Efetivar
                                    </button>
                                  )}
                                </>
                              )}
                              <button
                                onClick={() => openEditModal(contract)}
                                className="text-primary-600 hover:text-primary-900"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openDocumentModal(contract)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Adicionar documento"
                              >
                                <Upload className="w-4 h-4" />
                              </button>
                              {contract.status === 'ACTIVE' && !contract.is_indefinite && (
                                <>
                                  <button
                                    onClick={() => openRenewModal(contract)}
                                    className="text-green-600 hover:text-green-900"
                                    title="Renovar"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setContractToTerminate(contract.id);
                                      setConfirmTerminate(true);
                                    }}
                                    className="text-red-600 hover:text-red-900"
                                    title="Terminar"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => {
                                  setContractToDelete(contract.id);
                                  setConfirmDelete(true);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.map((alert, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <AlertTriangle className={`w-5 h-5 mr-2 ${alert.type === 'EXPIRED' ? 'text-red-500' : 'text-yellow-500'}`} />
                  <h3 className="font-medium text-gray-900">{alert.title}</h3>
                </div>
                <span className="bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full">
                  {alert.count}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-3">{alert.message}</p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Funcionário</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tipo</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Data</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {alert.contracts?.map((contract, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-sm text-gray-900">{contract.user_name}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{getContractTypeLabel(contract.contract_type)}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{formatDate(contract.end_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {alerts.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
              Nenhum alerta no momento. Todos os contratos estão em dia.
            </div>
          )}
        </div>
      )}

      {/* Modal de Criar/Editar Contrato */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {selectedContract ? 'Editar Contrato' : 'Novo Contrato'}
              </h2>
              <button onClick={() => { setShowModal(false); setSelectedContract(null); }} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={selectedContract ? handleUpdate : handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
                  <select
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    disabled={selectedContract}
                  >
                    <option value="">Selecione um funcionário</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.cpf})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contrato</label>
                  <select
                    value={formData.contract_type}
                    onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="CLT">CLT</option>
                    <option value="PJ">Pessoa Jurídica</option>
                    <option value="ESTAGIO">Estágio</option>
                    <option value="TEMPORARIO">Temporário</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Término</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      disabled={formData.is_indefinite}
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_indefinite"
                    checked={formData.is_indefinite}
                    onChange={(e) => setFormData({ ...formData, is_indefinite: e.target.checked, is_trial_period: e.target.checked ? false : formData.is_trial_period, end_date: e.target.checked ? '' : formData.end_date })}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <label htmlFor="is_indefinite" className="ml-2 text-sm text-gray-700">Prazo Indeterminado</label>
                </div>
                {formData.contract_type === 'CLT' && !formData.is_indefinite && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_trial_period"
                        checked={formData.is_trial_period}
                        onChange={(e) => setFormData({ ...formData, is_trial_period: e.target.checked })}
                        className="h-4 w-4 text-amber-600 border-gray-300 rounded"
                      />
                      <label htmlFor="is_trial_period" className="ml-2 text-sm font-semibold text-amber-900">
                        Contrato de Experiência (CLT Art. 445 - Limite de 90 dias)
                      </label>
                    </div>
                    {formData.is_trial_period && (
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <div>
                          <label className="block text-xs font-medium text-amber-800 mb-1">1º Período (dias)</label>
                          <input
                            type="number"
                            value={formData.trial_period_days_1}
                            onChange={(e) => setFormData({ ...formData, trial_period_days_1: parseInt(e.target.value, 10) || 45 })}
                            className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white"
                            min="1"
                            max="90"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-amber-800 mb-1">2º Período / Prorrogação (dias)</label>
                          <input
                            type="number"
                            value={formData.trial_period_days_2}
                            onChange={(e) => setFormData({ ...formData, trial_period_days_2: parseInt(e.target.value, 10) || 45 })}
                            className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white"
                            min="0"
                            max="89"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Salário</label>
                    <select
                      value={formData.salary_type}
                      onChange={(e) => setFormData({ ...formData, salary_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="MONTHLY">Mensalista</option>
                      <option value="HOURLY">Horista</option>
                      <option value="DAILY">Diarista</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salary_amount}
                      onChange={(e) => setFormData({ ...formData, salary_amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Carga Semanal (horas)</label>
                  <input
                    type="number"
                    value={formData.workload_weekly}
                    onChange={(e) => setFormData({ ...formData, workload_weekly: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="1"
                    max="60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setSelectedContract(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {selectedContract ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Renovação */}
      {showRenewModal && selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Renovar Contrato</h2>
              <button onClick={() => { setShowRenewModal(false); setSelectedContract(null); }} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleRenew}>
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Funcionário: <strong>{selectedContract.user_name}</strong></p>
                  <p className="text-sm text-gray-600">Tipo: <strong>{getContractTypeLabel(selectedContract.contract_type)}</strong></p>
                  <p className="text-sm text-gray-600">Vencimento: <strong>{formatDate(selectedContract.end_date)}</strong></p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data de Início</label>
                  <input
                    type="date"
                    value={renewData.new_start_date}
                    onChange={(e) => setRenewData({ ...renewData, new_start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data de Término</label>
                  <input
                    type="date"
                    value={renewData.new_end_date}
                    onChange={(e) => setRenewData({ ...renewData, new_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Novo Salário (opcional)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={renewData.new_salary_amount}
                    onChange={(e) => setRenewData({ ...renewData, new_salary_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder={selectedContract.salary_amount}
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowRenewModal(false); setSelectedContract(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Renovar Contrato
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Documento */}
      {showDocumentModal && selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Adicionar Documento</h2>
              <button onClick={() => { setShowDocumentModal(false); setSelectedContract(null); }} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleDocumentUpload}>
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Contrato: <strong>{selectedContract.user_name}</strong></p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento</label>
                  <select
                    value={documentData.document_type}
                    onChange={(e) => setDocumentData({ ...documentData, document_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="CONTRATO">Contrato Assinado</option>
                    <option value="TERMO_ADITIVO">Termo Aditivo</option>
                    <option value="ANEXO">Anexo</option>
                    <option value="OUTROS">Outros</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo</label>
                  <input
                    type="file"
                    onChange={(e) => setDocumentData({ ...documentData, document: e.target.files[0] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Formatos permitidos: PDF, DOC, DOCX, JPG, PNG</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowDocumentModal(false); setSelectedContract(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enviar Documento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmTerminate && (
        <ConfirmModal
          isOpen={confirmTerminate}
          title="Terminar Contrato"
          message="Deseja realmente terminar este contrato?"
          onConfirm={() => {
            terminateMutation.mutate(contractToTerminate);
            setConfirmTerminate(false);
            setContractToTerminate(null);
          }}
          onCancel={() => {
            setConfirmTerminate(false);
            setContractToTerminate(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          isOpen={confirmDelete}
          title="Excluir Contrato"
          message="Deseja realmente excluir este contrato? Esta ação não pode ser desfeita."
          onConfirm={() => {
            deleteMutation.mutate(contractToDelete);
            setConfirmDelete(false);
            setContractToDelete(null);
          }}
          onCancel={() => {
            setConfirmDelete(false);
            setContractToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default ContractManagement;